export type IncentivesMarketBeneficiaryAddress = {
  line1: string;
  zip: string;
  city: string;
  state?: string;
  country: string;
};

export type IncentivesMarketBeneficiary = {
  _id: string;
  name: string;
  email: string;
  cif?: string;
  image?: string;
  phone?: string;
  nationality?: string;
  birthdate?: string | Date;
  registerDate?: string | Date;
  address?: IncentivesMarketBeneficiaryAddress;
  bankAccountNumber?: string;
  partnerNumber?: number;
  user?: string;
  otherUsers?: string[];
  readOnlyUsers?: string[];
  plan?: string;
  sharesCount?: {
    future?: number;
    vested?: number;
    total?: number;
    actual?: number;
  };
  configuration?: {
    incentivesMarket?: {
      enabled?: boolean;
    };
  };
};

export type IncentiveOfferPlanClass = {
  _id: string;
  name?: string;
  isHidden?: boolean;
};

export enum IncentiveOfferType {
  BUY = 'BUY', // eslint-disable-line no-unused-vars
  SELL = 'SELL', // eslint-disable-line no-unused-vars
}

export enum IncentiveOfferStatus {
  DRAFT = 'DRAFT', // eslint-disable-line no-unused-vars
  PENDING = 'PENDING', // eslint-disable-line no-unused-vars
  ACTIVE = 'ACTIVE', // eslint-disable-line no-unused-vars
  ACCEPTED = 'ACCEPTED', // eslint-disable-line no-unused-vars
  REJECTED = 'REJECTED', // eslint-disable-line no-unused-vars
  EXPIRED = 'EXPIRED', // eslint-disable-line no-unused-vars
  CANCELED = 'CANCELED', // eslint-disable-line no-unused-vars
}

export type IncentiveOfferUnitsCount = {
  total: number;
  agreed: number;
  approved: number;
};

export type IncentiveOfferPublisherAudit = {
  serverCapturedAt?: Date | string;
  clientIp?: string;
  forwardedFor?: string;
  requestUserAgent?: string;
  clientContext?: Record<string, unknown>;
};

export type IncentiveOfferSellerParticipationLimits = {
  allowSellAll?: boolean;
  maxUnits?: number;
  maxPercentUnits?: number;
  maxPhantoms?: number;
  maxPercentPhantoms?: number;
  maxStocks?: number;
  maxPercentStocks?: number;
  maxWarrants?: number;
  maxPercentWarrants?: number;
  maxAmount?: number;
  maxPercentUnvested?: number;
};

export type IncentiveOffer = {
  _id: string;
  society: string;
  beneficiary: string | Partial<IncentivesMarketBeneficiary>;
  unitClass: string | Partial<IncentiveOfferPlanClass> | null;
  emailSubsidiary?: string;
  documents: Array<
    string | { _id: string; name?: string; description?: string }
  >;
  unitsCount: IncentiveOfferUnitsCount;
  unitPrice: number;
  startDate: Date | string;
  endDate: Date | string;
  calculationDate?: Date | string;
  type: IncentiveOfferType;
  isPartialAmountEnabled: boolean;
  minUnitsPerAcceptance?: number;
  isSellerNameHidden: boolean;
  sellerParticipationLimits?: IncentiveOfferSellerParticipationLimits;
  createdAt: Date | string;
  updatedAt: Date | string;
  offerPublisherAudit?: IncentiveOfferPublisherAudit;
  activationNotifiedAt?: Date | string;
  expiryNotifiedAt?: Date | string;
  canceledAt?: Date | string | null;
  canceledBy?: string | null;
};

export type BeneficiaryAgreedIncentiveOffer = {
  incentiveOffer: string | IncentiveOffer;
  unitsCount: { agreed: number };
  date: Date | string;
  isApproved: boolean;
  documents: string[];
};

export type IncentiveOffersCount = {
  buy: number;
  sell: number;
  total: number;
};

export type IncentiveOffersSummary = {
  offersCount: number;
  unitsCount: number;
  totalAmount: number;
};

export enum IncentiveOfferOperationStatus {
  PENDING = 'PENDING', // eslint-disable-line no-unused-vars
  KYC_REVIEW = 'KYC_REVIEW', // eslint-disable-line no-unused-vars
  VALIDATION = 'VALIDATION', // eslint-disable-line no-unused-vars
  PAYMENT_PENDING = 'PAYMENT_PENDING', // eslint-disable-line no-unused-vars
  TRANSFER_PENDING = 'TRANSFER_PENDING', // eslint-disable-line no-unused-vars
  COMPLETED = 'COMPLETED', // eslint-disable-line no-unused-vars
  CANCELLED = 'CANCELLED', // eslint-disable-line no-unused-vars
  REJECTED = 'REJECTED', // eslint-disable-line no-unused-vars
}

export type IncentiveOfferOperationComment = {
  _id: string;
  user: string | { _id?: string; name?: string; email?: string };
  role?: 'BUYER' | 'SELLER' | 'ADMIN';
  text: string;
  createdAt: Date | string;
  updatedAt: Date | string;
};

export type IncentiveOfferOperationStatusHistory = {
  status: IncentiveOfferOperationStatus;
  date: Date | string;
  user?: string;
  comment?: string;
};

export type IncentiveOfferDocumentRequestType =
  | 'ID'
  | 'PAYMENT_PROOF'
  | 'OTHER';

export type IncentiveOfferOperationDocumentRequest = {
  documentType: IncentiveOfferDocumentRequestType;
  description?: string;
  deadlineBusinessDays?: number;
  targetRole?: 'BUYER' | 'SELLER' | 'BOTH';
  requestedAt?: Date | string;
};

export type IncentiveOfferOperationParticipantAudit = {
  serverCapturedAt?: Date | string;
  clientIp?: string;
  forwardedFor?: string;
  requestUserAgent?: string;
  clientContext?: Record<string, unknown>;
};

export type IncentiveOfferOperation = {
  _id: string;
  society: string;
  incentiveOffer: string | Partial<IncentiveOffer>;
  buyer: string | Partial<IncentivesMarketBeneficiary>;
  seller: string | Partial<IncentivesMarketBeneficiary>;
  unitClass: string | Partial<IncentiveOfferPlanClass>;
  unitsCount: number;
  unitPrice: number;
  totalPrice: number;
  status: IncentiveOfferOperationStatus;
  requestDate: Date | string;
  completionDate?: Date | string;
  buyerDocuments: (string | { _id: string; name?: string })[];
  sellerDocuments: (string | { _id: string; name?: string })[];
  buyerSignature?: string;
  sellerSignature?: string;
  incentivesMarketPurchaseContract?: string | { _id: string; name?: string };
  comments: IncentiveOfferOperationComment[];
  statusHistory: IncentiveOfferOperationStatusHistory[];
  documentRequests?: IncentiveOfferOperationDocumentRequest[];
  offerAmount?: number;
  createdAt: Date | string;
  updatedAt: Date | string;
  operationParticipantAudit?: IncentiveOfferOperationParticipantAudit;
};

export type BeneficiarySharesCount = {
  future?: number;
  vested?: number;
  total?: number;
  actual?: number;
};

export type BeneficiaryShares = {
  total: number;
  acquisitionValue: number;
  avgAcquisitionPrice: number;
  estimatedValue: number;
  valueChange: number;
};

export type IncentiveOfferPriceContext = {
  currentOfferPrice: number;
  lastTransactionPrice: number;
  avgSellOffersPrice: number;
  officialValuation: number;
  userAvgBuyPrice: number;
};

export type IncentiveOfferTimelineStep = {
  key: string;
  label: string;
  icon?: string;
  date?: string;
  isCompleted: boolean;
  isCurrent: boolean;
};

export type IncentiveOfferSellerInfo = {
  beneficiaryId: string;
  name: string;
  image?: string;
  memberSince: string;
  totalShares: number;
  capitalPercentage: number;
  previousOperations: number;
  rating: number;
};

export type BeneficiaryActiveOffers = {
  total: number;
  sellCount: number;
  buyCount: number;
  sellAmount: number;
  buyAmount: number;
};

export type BeneficiaryOperations = {
  total: number;
  sellCount: number;
  buyCount: number;
  sellAmount: number;
  buyAmount: number;
};

export type MarketInfo = {
  totalOffers: number;
  sellCount: number;
  buyCount: number;
  sellAmount: number;
  buyAmount: number;
};

export type ReferenceInfo = {
  lastValuation: number;
  lastValuationDate: string;
  totalShares: number;
  lastTransactionPrice: number;
  lastTransactionDate: string;
  activeBeneficiariesInMarket: number;
};

export type RecentActivity = {
  _id: string;
  type: 'BUY' | 'SELL';
  unitsCount: number;
  unitClass: string;
  pricePerShare: number;
  totalAmount: number;
  date: Date | string;
  daysAgo: string;
};

export type IncentivesMarketBeneficiaryShares = BeneficiaryShares;
export type IncentivesMarketBeneficiaryActiveOffers = BeneficiaryActiveOffers;
export type IncentivesMarketBeneficiaryOperations = BeneficiaryOperations;

export type IncentivesMarketContextType = {
  data: {
    societyId: string;
    societyName: string;
    beneficiaryShares: BeneficiaryShares;
    beneficiaryActiveOffers: BeneficiaryActiveOffers;
    beneficiaryOperations: BeneficiaryOperations;
    marketInfo: MarketInfo;
    sellOffers: IncentiveOffer[];
    buyOffers: IncentiveOffer[];
    sellOffersSummary: IncentiveOffersSummary;
    buyOffersSummary: IncentiveOffersSummary;
    recentActivity: RecentActivity[];
    referenceInfo: ReferenceInfo;
    isAdmin: boolean;
    isBeneficiary: boolean;
    isReadOnly: boolean;
    canCreateOffer: boolean;
    isLoading: boolean;
    isMarketClosed: boolean;
  };
  actions: {
    handleCreateSellOffer: VoidFunction;
    handleCreateBuyOffer: VoidFunction;
    handleViewSellOffers: VoidFunction;
    handleViewBuyOffers: VoidFunction;
    handleViewOfferDetail: (offerId: string) => void;
    handleViewMyShares: VoidFunction;
    handleViewMyOffers: VoidFunction;
    handleViewMyOperations: VoidFunction;
    handleOpenHowItWorks: VoidFunction;
  };
};

export type Beneficiary = IncentivesMarketBeneficiary;
export type Partner = IncentivesMarketBeneficiary;

export type {
  IncentivesMarketBeneficiaryContext,
  IncentivesMarketPlanContext,
} from './IncentivesMarketBeneficiaryContext';
